import { html } from '@codemirror/lang-html';
import { SettingKey, SettingsItem, WelcomeMessagePosition } from "@dto/settings.dto";
import { vscodeDark } from '@uiw/codemirror-theme-vscode';
import CodeMirror from '@uiw/react-codemirror';
import { Badge, Button, Form, FormItemProps, Input, InputNumber, Switch, Tabs } from "antd";
import { FormProps, Rule } from "antd/es/form";
import { useForm } from "antd/es/form/Form";
import type { NamePath } from "antd/es/form/interface";
import { FC, PropsWithChildren, useEffect, useRef, useState } from "react";
import { useAppGlobStore } from "src/appGlobalStore";
import { XmSelect } from "src/component/select/XmSelect";
import { CustomFormCompProps } from "src/component/selectItems/comp/Items";
import { XmSpinner } from "src/component/spinner/XmSpinner";
import { Label } from "src/data/Label";
import { GLOB } from "src/util/Glob";
import { ThemeUtil } from "src/util/ThemeUtil";
import XmTimeZoneSelect from "../../../component/timezoneSelect/XmTimeZoneSelect";
import { BasicFormProps } from "../../../data/props";
import { Log } from "../../../service/log.service";
import { getSettings, saveSettings } from "../../../service/settings.service";

export enum SEPARATORS_CSV {
  SEMICOLON = ';',
  COMMA = ',',
  COLON = ':',
  PIPE = '|'
}
export enum SEPARATORS_DECIMAL {
  COMMA = ',',
  DOT = '.'
}

const LOG_INTERVAL_SECONDS = 60 * 60;

const keys = SettingKey.GLOB;

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
};

type TabKey =
  | 'security'
  | 'graph'
  | 'logs'
  | 'snmp'
  | 'retention'
  | 'separators'
  | 'branding'
  | 'events';

const TAB_ORDER: readonly TabKey[] = [
  'security', 'graph', 'logs', 'snmp', 'retention', 'separators', 'branding', 'events',
];

// IMPORTANT: When adding a new field to OptionsForm, also add an entry here
// so it gets navigation/indicator support.
const FIELD_PANEL_MAP: Record<string, TabKey> = {
  [keys.get('TOKEN_EXPIRATION')]: 'security',
  [keys.get('FAILED_LOGIN_ATTEMPTS_LIMIT')]: 'security',
  [keys.get('TOP_ITEMS')]: 'graph',
  [keys.get('SHOW_DELETED_ITEMS')]: 'graph',
  [keys.get('LOG_INTERVAL')]: 'logs',
  [keys.get('SNMP_TRAP_HOST')]: 'snmp',
  [keys.get('SNMP_COMMUNITY')]: 'snmp',
  [keys.get('REMOVE_FROM_MENU')]: 'retention',
  [keys.get('DATA_RETENTION')]: 'retention',
  [keys.get('DECIMAL_SEPARATOR')]: 'separators',
  [keys.get('CSV_SEPARATOR')]: 'separators',
  [keys.get('DEFAULT_TIMEZONE')]: 'branding',
  [keys.get('INSTANCE_LABEL')]: 'branding',
  GLOB_WELCOME_MESSAGE_ENABLED: 'branding',
  [keys.get('WELCOME_MESSAGE')]: 'branding',
  GLOB_WELCOME_MESSAGE_POSITION: 'branding',
  [keys.get('EVENT_INTERVAL')]: 'events',
};

function computeInvalidTabs(errorFields: { name: NamePath; errors: string[]; }[]): Set<TabKey> {
  const set = new Set<TabKey>();
  for (const { name, errors } of errorFields) {
    if (!errors?.length) continue;
    const fieldName = Array.isArray(name) ? String(name[0]) : String(name);
    const tab = FIELD_PANEL_MAP[fieldName];
    if (tab) set.add(tab);
  }
  return set;
}

export const GlobalOptions: FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const initValues = useRef<SettingsItem>();

  useEffect(() => {
    void getSettings(keys.PREFIX).then(({ data: { data: settings } }) => {
      keys.reType(settings);
      initValues.current = {
        ...settings,
        [keys.get('FAILED_LOGIN_ATTEMPTS_LIMIT')]: Number(settings[keys.get('FAILED_LOGIN_ATTEMPTS_LIMIT')]),
        [keys.get('LOG_INTERVAL')]: settings.GLOB_LOG_INTERVAL ? Number(settings.GLOB_LOG_INTERVAL) / LOG_INTERVAL_SECONDS : settings.GLOB_LOG_INTERVAL
      };
    }).finally(() => setIsLoading(false));
  }, []);

  if (isLoading)
    return <XmSpinner />;

  return <OptionsForm {...BasicFormProps} initialValues={initValues.current} />;
};

function useSaveCfg() {
  const updateWelcomeMsg = useAppGlobStore(state => state.updateWelcomeMsg);
  return function saveCfg(values: SettingsItem) {
    if (!values[keys.get('WELCOME_MESSAGE')])
      values[keys.get('WELCOME_MESSAGE_ENABLED')] = 'false';

    if (values.GLOB_LOG_INTERVAL) {
      values.GLOB_LOG_INTERVAL = LOG_INTERVAL_SECONDS * Number(values.GLOB_LOG_INTERVAL) + '';
    }

    void saveSettings(values).then(() => {
      GLOB.graphTopItems = parseInt(values.GLOB_TOP_ITEMS);
      GLOB.instanceLabel = values.GLOB_INSTANCE_LABEL ?? '';
      GLOB.updateTitle();
      updateWelcomeMsg({
        GLOB_WELCOME_MESSAGE: values[keys.get('WELCOME_MESSAGE')],
        GLOB_WELCOME_MESSAGE_POSITION: values[keys.get('WELCOME_MESSAGE_POSITION')],
        GLOB_WELCOME_MESSAGE_ENABLED: values[keys.get('WELCOME_MESSAGE_ENABLED')],
      });

      Log.info('Settings saved');
    });
  };
}

function useUniqueSeparatorRule(form: ReturnType<typeof useForm<SettingsItem>>[0]): Rule {
  return {
    validator: (rule, value: string) => {
      if (!value || form.getFieldValue(keys.get('DECIMAL_SEPARATOR')) !== form.getFieldValue(keys.get('CSV_SEPARATOR')))
        return Promise.resolve();

      return Promise.reject(new Error("Decimal and CSV separator must differ"));
    }
  };
}

const OptionsForm = ({ ...formProps }: FormProps<SettingsItem>) => {
  const [form] = useForm<SettingsItem>();
  const saveCfg = useSaveCfg();
  const [activeTab, setActiveTab] = useState<TabKey>('security');
  const [invalidTabs, setInvalidTabs] = useState<Set<TabKey>>(new Set());
  const { GLOB_WELCOME_MESSAGE, ...initValues } = formProps.initialValues;
  const UNIQUE_SEPARATOR_RULE = useUniqueSeparatorRule(form);

  const renderTabLabel = (key: TabKey, label: string) => (
    <Badge dot={invalidTabs.has(key)} status='error' offset={[3, 0]}>
      {label}
    </Badge>
  );

  return (
    <Form<SettingsItem>
      className='xm-form xm-options-form'
      {...formProps}
      initialValues={{
        'GLOB_SHOW_DELETED_ITEMS': true,
        'GLOB_EVENT_INTERVAL': 60,
        'GLOB_WELCOME_MESSAGE_POSITION': WelcomeMessagePosition.RIGHT,
        'GLOB_WELCOME_MESSAGE': GLOB_WELCOME_MESSAGE || customLoginDef,
        ...initValues,
      }}
      form={form}
      onFinish={(values) => { setInvalidTabs(new Set()); saveCfg(values); }}
      onValuesChange={(changedValues, allValues) => {
        if (!changedValues.GLOB_WELCOME_MESSAGE_ENABLED && !allValues.GLOB_WELCOME_MESSAGE)
          form.setFieldValue(['GLOB_WELCOME_MESSAGE'], customLoginDef);
      }}
      {...layout}
      onFinishFailed={({ errorFields }) => {
        const tabSet = computeInvalidTabs(errorFields);
        setInvalidTabs(tabSet);
        const first = TAB_ORDER.find(t => tabSet.has(t));
        if (first) setActiveTab(first);
      }}
      onFieldsChange={() => {
        setInvalidTabs(computeInvalidTabs(form.getFieldsError()));
      }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as TabKey)}
        destroyOnHidden={false}
        items={[
          { key: 'security', label: renderTabLabel('security', 'Security'), forceRender: true, children: securityFields() },
          { key: 'graph', label: renderTabLabel('graph', 'Graph'), forceRender: true, children: graphFields() },
          { key: 'logs', label: renderTabLabel('logs', 'Logs'), forceRender: true, children: logsFields() },
          { key: 'snmp', label: renderTabLabel('snmp', 'SNMP'), forceRender: true, children: snmpFields() },
          { key: 'retention', label: renderTabLabel('retention', 'Data Retention'), forceRender: true, children: retentionFields() },
          { key: 'separators', label: renderTabLabel('separators', 'Separators'), forceRender: true, children: separatorsFields(UNIQUE_SEPARATOR_RULE) },
          { key: 'branding', label: renderTabLabel('branding', 'Customization & Localization'), forceRender: true, children: brandingFields() },
          { key: 'events', label: renderTabLabel('events', 'Events'), forceRender: true, children: eventsFields() },
        ]}
      />
      <div className={`xm-options-form-footer xm-options-form-footer--${activeTab}`}>
        <Button type="primary" htmlType="submit">Save</Button>
      </div>
    </Form>
  );
};


function securityFields() {
  return (
    <>
      <FormItemOption name={keys.get('TOKEN_EXPIRATION')}
        label='Session timeout'
        rules={[{ required: true, type: 'number', min: 0 }]}
        description={<>Inactivity timeout after which user is logged out.<br />Set 0 for indefinite.</>}
      >
        <InputNumber min={0} step={5} addonAfter='min' className='xm-opt-session-timeout' />
      </FormItemOption>
      <FormItemOption name={keys.get('FAILED_LOGIN_ATTEMPTS_LIMIT')}
        label='Failed logins attempts limit'
        rules={[
          { required: true, message: 'This field is required' },
          { type: 'number', min: 1, message: 'Must be at least 1' }
        ]}
        description='Number of failed login attempts before an account is locked.'
      >
        <InputNumber min={1} step={1} className='xm-opt-failed-logins' />
      </FormItemOption>
    </>
  );
}

function graphFields() {
  return (
    <>
      <FormItemOption name={keys.get('TOP_ITEMS')}
        label='Default graph items limit'
        rules={[{ required: true, type: 'number', min: 0 }]}
        description={<>{Label.GRAPH_TOP_ITEMS_TOOLTIP}<br />Can be overridden in user profile.</>}
      >
        <InputNumber min={0} step={10} className='xm-opt-graph-items' />
      </FormItemOption>
      <FormItemOption name={keys.get('SHOW_DELETED_ITEMS')}
        label="Show deleted items in graphs"
        rules={[{ required: false }]}
        valuePropName='checked'
        noStyle={false}
      >
        <Switch />
      </FormItemOption>
    </>
  );
}

function logsFields() {
  return (
    <FormItemOption name={keys.get('LOG_INTERVAL')}
      label="Default log interval"
      rules={[{ required: false, type: 'number', min: 0 }]}
      description={<>How far back to load log entries by default.<br />If unset, uses interval from page (default: day).</>}
    >
      <InputNumber min={0} step={1} addonAfter='hours' className='xm-opt-log-interval' />
    </FormItemOption>
  );
}

function snmpFields() {
  return (
    <>
      <FormItemOption name={keys.get('SNMP_TRAP_HOST')}
        label='SNMP trap host'
        rules={[{
          message: "Contains invalid address!",
          pattern: /^((((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)\.?\b){4})|((([a-zA-Z_]|[a-zA-Z][a-zA-Z0-9_\-]*[a-zA-Z0-9_])\.)*([A-Za-z0-9_]|[A-Za-z0-9_][A-Za-z0-9_\-]*[A-Za-z0-9_])))(,((((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)\.?\b){4})|((([a-zA-Z_]|[a-zA-Z][a-zA-Z0-9_\-]*[a-zA-Z0-9_])\.)*([A-Za-z0-9_]|[A-Za-z0-9_][A-Za-z0-9_\-]*[A-Za-z0-9_]))))*$/
        }]}
        description={<>Hostname or IP of SNMP trap receiver.<br />Add multiple hosts separated by comma, e.g. &apos;host1,host2&apos;.</>}
      >
        <CustomHostInput />
      </FormItemOption>
      <FormItemOption name={keys.get('SNMP_COMMUNITY')}
        label='SNMP community string'
        description='Auth token sent with each SNMP request.'
      >
        <Input className='xm-opt-snmp-community' />
      </FormItemOption>
    </>
  );
}

function retentionFields() {
  return (
    <>
      <FormItemOption name={keys.get('REMOVE_FROM_MENU')}
        label='Remove from UI after'
        rules={[{ required: false, type: 'number', min: 0 }]}
        description={<>How long do inactive devices remain visible in the UI.<br />Set 0 for indefinite.</>}
      >
        <InputNumber min={0} step={1} addonAfter='days' className='xm-opt-retention' />
      </FormItemOption>
      <FormItemOption name={keys.get('DATA_RETENTION')}
        label='Delete from database after'
        rules={[{ required: false, type: 'number', min: 0 }]}
        description={<>How long does data for inactive devices remain in the database before it is deleted<br />Set 0 for indefinite.</>}
      >
        <InputNumber min={0} step={1} addonAfter='days' className='xm-opt-retention' />
      </FormItemOption>
    </>
  );
}

function separatorsFields(uniqueRule: Rule) {
  return (
    <>
      <FormItemOption name={keys.get('DECIMAL_SEPARATOR')}
        label='Decimal separator'
        dependencies={[keys.get('CSV_SEPARATOR')]}
        rules={[{ required: true }, uniqueRule]}
        description='Decimal separator used in numbers.'
      >
        <XmSelect
          className='xm-opt-separator'
          options={Object.values(SEPARATORS_DECIMAL).map(c => ({ label: c, value: c }))}
          placeholder={Object.values(SEPARATORS_DECIMAL).join(' ')}
        />
      </FormItemOption>
      <FormItemOption name={keys.get('CSV_SEPARATOR')}
        label='CSV separator'
        dependencies={[keys.get('DECIMAL_SEPARATOR')]}
        rules={[{ required: true }, uniqueRule]}
        description='Character used to separate values in exported CSV files.'
      >
        <XmSelect
          className='xm-opt-separator'
          options={Object.values(SEPARATORS_CSV).map(c => ({ label: c, value: c }))}
          placeholder={Object.values(SEPARATORS_CSV).join(' ')}
        />
      </FormItemOption>
    </>
  );
}

function brandingFields() {
  return (
    <>
      <FormItemOption name={keys.get('DEFAULT_TIMEZONE')}
        label='Default timezone'
        rules={[{ required: true }]}
        description='Timezone can be overridden in user profile.'
      >
        <XmTimeZoneSelect local={GLOB.userInfo.timezone} className='xm-opt-timezone' />
      </FormItemOption>
      <FormItemOption name={keys.get('INSTANCE_LABEL')}
        label='Browser tab label'
        description={`Replaces "${Label.XORMON_NG}" in the browser tab title. Useful to distinguish multiple instances.`}
      >
        <Input maxLength={32} className='xm-opt-tab-label' placeholder={Label.XORMON_NG} />
      </FormItemOption>
      <Form.Item<SettingsItem> shouldUpdate={(prev, now) => prev.GLOB_WELCOME_MESSAGE_ENABLED !== now.GLOB_WELCOME_MESSAGE_ENABLED} noStyle>
        {({ getFieldValue }) => (
          <>
            <FormItemOption name={'GLOB_WELCOME_MESSAGE_ENABLED'}
              label="Custom login banner"
              valuePropName='checked'
            >
              <Switch />
            </FormItemOption>

            <FormItemOption name={keys.get('WELCOME_MESSAGE')}
              className="xm-form-item-welcome-msg"
              dependencies={['GLOB_WELCOME_MESSAGE_ENABLED']}
              rules={[{ required: getFieldValue('GLOB_WELCOME_MESSAGE_ENABLED'), message: '' }]}
              wrapperCol={{ offset: 6, span: 18 }}
            >
              <CustomWelcomeMsg isEnabled={getFieldValue('GLOB_WELCOME_MESSAGE_ENABLED')} />
            </FormItemOption>

            <FormItemOption name={'GLOB_WELCOME_MESSAGE_POSITION'}
              rules={[{ required: false }]}
              label="Position"
            >
              <XmSelect<WelcomeMessagePosition>
                disabled={!getFieldValue('GLOB_WELCOME_MESSAGE_ENABLED')}
                className='xm-opt-position'
                options={Object.keys(WelcomeMessagePosition).map(key => {
                  const value = WelcomeMessagePosition[key];
                  const label = value.charAt(0).toUpperCase() + value.slice(1);
                  return { label, value };
                })}
              />
            </FormItemOption>
          </>
        )}
      </Form.Item>
    </>
  );
}

function eventsFields() {
  return (
    <FormItemOption name={keys.get('EVENT_INTERVAL')}
      label='HW Event interval'
      rules={[{ required: false, type: 'number', min: 5, max: 60 }]}
      description={<>How long after a status change the HW event is sent.<br />Allowed range: 5–60 min.</>}
    >
      <InputNumber min={5} max={60} addonAfter='min' className='xm-opt-event-interval' />
    </FormItemOption>
  );
}

interface CustomWelcomeMsgProps extends CustomFormCompProps<string> {
  isEnabled?: boolean;
}

const CustomWelcomeMsg = ({ value, onChange, isEnabled }: CustomWelcomeMsgProps) => {
  return (
    <CodeMirror
      className="xm-html-editor"
      value={value}
      height="13em"
      extensions={[html()]}
      theme={ThemeUtil.isDarkMode() ? vscodeDark : 'light'}
      readOnly={!isEnabled}
      onChange={onChange}
      basicSetup={{
        lineNumbers: true,
        foldGutter: true,
        dropCursor: true,
        allowMultipleSelections: true,
        indentOnInput: true,
        syntaxHighlighting: true,
        bracketMatching: true,
        autocompletion: true,
        rectangularSelection: true,
        crosshairCursor: true,
        highlightActiveLine: true,
        highlightSelectionMatches: true,
        closeBrackets: true,
      }}
    />
  );
};

const customLoginDef = `<div style="background: #fff; border: 1px solid #d9d9d9; padding-bottom: 5em">
  <h1 style="background: #2b4f60; text-align: center; color: #f0f0f0ff">Custom Banner Example</h1>
  <p style="padding: 0.3em">This banner is for demonstration purposes only. Replace it with your own.</p>
</div>`;

interface FFormItemOptionProps extends PropsWithChildren<FormItemProps> {
  description?: React.ReactNode;
}

const FormItemOption = ({ children, description, label, ...rest }: FFormItemOptionProps) => {
  const { wrapperCol, ...restProps } = rest;
  return (
    <Form.Item label={label} wrapperCol={wrapperCol}>
      <Form.Item noStyle {...restProps}>
        {children}
      </Form.Item>
      {description && <div className='xm-options-form-desc'>{description}</div>}
    </Form.Item>
  );
};

const CustomHostInput = ({ value, onChange }: CustomFormCompProps<string>) => {
  const val: string[] = value?.length ? value.split(',') : [];
  const handleChange = (value: string[]) => {
    if (value?.length) {
      onChange?.(value.join(','));
      return;
    }

    onChange?.('');
  };

  return <XmSelect<string[]>
    className='xm-opt-host-input'
    mode="tags"
    value={val}
    options={val.map(host => ({ label: host }))}
    onChange={handleChange}
    tokenSeparators={[',']}
    dropdownStyle={{ display: 'none' }}
    ignoreReadonly
  />;
};
